import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, MapPin, Send, X, Phone, MessageCircle, Camera } from 'lucide-react';

interface PublicSOSProps {
  onSendSOS?: (sosData: any) => void;
}

const PublicSOSButton: React.FC<PublicSOSProps> = ({ onSendSOS }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState<'high' | 'medium' | 'low'>('high');
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  const getLocation = () => {
    setIsGettingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setIsGettingLocation(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          setIsGettingLocation(false);
          // Default location if denied
          setLocation({ lat: 12.9716, lng: 77.5946 });
        }
      );
    } else {
      setLocation({ lat: 12.9716, lng: 77.5946 });
      setIsGettingLocation(false);
    }
  };

  const handleSendSOS = () => {
    const sosData = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      location: location || { lat: 12.9716, lng: 77.5946 },
      message: message || 'Emergency SOS',
      priority,
      status: 'active'
    };

    if (onSendSOS) {
      onSendSOS(sosData);
    }

    // Close modal after sending
    setTimeout(() => {
      setIsOpen(false);
      setMessage('');
      setPriority('high');
    }, 2000);
  };

  return (
    <>
      {/* Floating SOS Button */}
      <motion.button
        className="fixed bottom-8 right-8 z-40 w-16 h-16 bg-critical rounded-full flex items-center justify-center shadow-2xl"
        style={{ boxShadow: '0 0 30px #D72638' }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        animate={{ 
          scale: [1, 1.1, 1],
          boxShadow: ['0 0 30px #D72638', '0 0 50px #D72638', '0 0 30px #D72638']
        }}
        transition={{ duration: 2, repeat: Infinity }}
        onClick={() => {
          setIsOpen(true);
          getLocation();
        }}
      >
        <AlertTriangle size={32} className="text-white" />
      </motion.button>

      {/* SOS Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
              className="cyber-card w-96 p-8 border-critical/50"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-critical flex items-center gap-2">
                  <AlertTriangle size={24} />
                  SOS EMERGENCY
                </h2>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="hover:bg-white/10 p-2 rounded-lg"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4">
                {/* Location Status */}
                <div className="p-4 bg-black/30 rounded-lg border border-critical/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-400 flex items-center gap-2">
                      <MapPin size={16} />
                      Your Location
                    </span>
                    {!location && (
                      <button
                        onClick={getLocation}
                        className="text-xs text-cyan-500 hover:underline"
                        disabled={isGettingLocation}
                      >
                        {isGettingLocation ? 'Getting...' : 'Get Location'}
                      </button>
                    )}
                  </div>
                  {location ? (
                    <p className="text-sm font-mono">
                      {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-500">Waiting for location...</p>
                  )}
                </div>

                {/* Message Input */}
                <div>
                  <label className="text-sm text-gray-400 mb-1 block flex items-center gap-2">
                    <MessageCircle size={16} />
                    Message (Optional)
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Describe your emergency..."
                    rows={3}
                    className="w-full bg-black/50 border border-gray-700 rounded-lg px-4 py-3 text-white"
                  />
                </div>

                {/* Priority Selection */}
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Priority Level</label>
                  <div className="grid grid-cols-3 gap-2">
                    {(['high', 'medium', 'low'] as const).map((p) => (
                      <button
                        key={p}
                        onClick={() => setPriority(p)}
                        className={`py-2 px-3 rounded-lg text-sm font-bold uppercase transition-all ${
                          priority === p
                            ? p === 'high' 
                              ? 'bg-critical text-white'
                              : p === 'medium'
                                ? 'bg-orange-500 text-white'
                                : 'bg-yellow-500 text-black'
                            : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 mt-6">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex-1 bg-critical hover:bg-critical/80 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2"
                    style={{ boxShadow: '0 0 20px #D72638' }}
                    onClick={handleSendSOS}
                  >
                    <Send size={16} />
                    SEND SOS
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2"
                    onClick={() => setIsOpen(false)}
                  >
                    <Phone size={16} />
                    CALL
                  </motion.button>
                </div>

                <p className="text-xs text-gray-500 text-center mt-4">
                  Your SOS will be sent to the nearest rescue team immediately
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PublicSOSButton;